import { DateTime } from 'luxon';

import { IKeyValueAPI } from '@/common/models/misc-api-models/IKeyValueAPI';
import { DASH } from '@/globals';
import Validatable from '@/validator/Validatable';

interface IJourneyAnalysisAPI {
  journeyAnalysis_id: number;
  name: string;
  description: string;
  tags: string[];
  organization_id: number;
  created_on_utc?: string;
  created_by: string;
  modified_on_utc?: string;
  modified_by: string;
  properties: IKeyValueAPI[];
  start_date: string;
  end_date: string;
}

export default class JourneyAnalysis extends Validatable {
  id: number;

  name: string;

  description: string;

  tags: string[];

  organizationID: number;

  createdOnUtc?: DateTime;

  createdBy: string;

  modifiedOnUtc?: DateTime;

  modifiedBy: string;

  properties: IKeyValueAPI[];

  startDate: DateTime;

  endDate: DateTime;

  apiObject?: IJourneyAnalysisAPI;

  timestamp: DateTime;

  constructor(
    id = -1,
    name: string,
    description = '',
    tags: string[] = null, // Define the appropriate type for tags
    organizationID: number,
    createdOnUtc: DateTime,
    createdBy: string,
    modifiedOnUtc: DateTime,
    modifiedBy: string,
    properties: IKeyValueAPI[] = [], // Define the appropriate type for properties
    startDate: DateTime = DateTime.local().startOf('week').plus({ days: -7 }),
    endDate: DateTime = DateTime.local().startOf('week').plus({ days: -1 }),
  ) {
    super();
    this.id = id;
    this.name = name;
    this.description = description;
    this.tags = tags;
    this.organizationID = organizationID;
    this.createdOnUtc = createdOnUtc;
    this.createdBy = createdBy;
    this.modifiedOnUtc = modifiedOnUtc;
    this.modifiedBy = modifiedBy;
    this.properties = properties;
    this.startDate = startDate;
    this.endDate = endDate;
    this.apiObject = null;
    this.timestamp = DateTime.local();
  }

  get originalObject(): JourneyAnalysis {
    if (this.apiObject !== null) {
      return JourneyAnalysis.fromApi(this.apiObject);
    }
    return null;
  }

  get endpoint(): string {
    return `/journeys/${this.id}`;
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc !== null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc !== null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get features(): any[] {
    return this.geofences((x) => x.feature);
  }

  get isFromDataFile(): boolean {
    return this.properties.find((x) => x.key === 'isFromDataFile')?.value || false;
  }

  set isFromDataFile(value: boolean) {
    const index = this.properties.findIndex((x) => x.key === 'isFromDataFile');
    if (index >= 0) {
      this.properties[index].value = value;
    } else {
      this.properties.push({ key: 'isFromDataFile', value });
    }
  }

  get dataFileName(): string {
    return this.properties.find((x) => x.key === 'dataFileName')?.value || DASH;
  }

  set dataFileName(value: string) {
    const index = this.properties.findIndex((x) => x.key === 'dataFileName');
    if (index >= 0) {
      this.properties[index].value = value;
    } else {
      this.properties.push({ key: 'dataFileName', value });
    }
  }

  get fileDataRows() {
    return this.properties.find((x) => x.key === 'fileDataRows')?.value || null;
  }

  set fileDataRows(value) {
    const index = this.properties.findIndex((x) => x.key === 'fileDataRows');
    if (index >= 0) {
      this.properties[index].value = value;
    } else {
      this.properties.push({ key: 'fileDataRows', value });
    }
  }

  clone(): JourneyAnalysis {
    const apiModel = this.toApi();
    apiModel.journeyAnalysis_id = -1;
    apiModel.name = `Copy of ${apiModel.name}`;
    return JourneyAnalysis.fromApi(apiModel);
  }

  static fromObject(obj: any): JourneyAnalysis {
    return super.fromObject(obj, new JourneyAnalysis());
  }

  static fromApi(apiObject: IJourneyAnalysisAPI): JourneyAnalysis {
    return JourneyAnalysis.fromObject({
      id: apiObject.journeyAnalysis_id,
      name: apiObject.name,
      description: apiObject.description,
      tags: apiObject.tags,
      organizationID: apiObject.organization_id,
      apiObject,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      properties: apiObject.properties,
      startDate:
        apiObject.start_date != null
          ? DateTime.fromISO(apiObject.start_date)
          : DateTime.local().startOf('week').plus({ days: -7 }),
      endDate:
        apiObject.end_date != null
          ? DateTime.fromISO(apiObject.end_date)
          : DateTime.local().startOf('week').plus({ days: -1 }),
    });
  }

  toApi(): IJourneyAnalysisAPI {
    return {
      journeyAnalysis_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      description: this.description,
      tags: this.tags,
      created_on_utc: this.createdOnUtc,
      created_by: this.createdBy,
      modified_on_utc: this.modifiedOnUtc,
      modified_by: this.modifiedBy,
      properties: this.properties,
      start_date: this.startDate,
      end_date: this.endDate,
    };
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required',
    };
  }
}
